/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl } from "@Utils";
import { useTranslation } from "@i18n";
import { convertDate } from "@Services/luxonHelper";
import React, { useState, useEffect } from "react";
import SelectDropDown, { type SelectEventArgs } from "@CubeComponents/Mui_Form/SelectDropDown";
import { BsThreeDotsVertical } from "react-icons/bs";
import axiosWrapper from "@AxiosWrapper";
import type {
  GetEstimateFilesResponse,
  EstimateVersionEntry,
  DeleteEstimateVersionNoteRequest,
  DeleteEstimateVersionNoteResponse,
  EstimateData as EstimateDataState,
} from "@ServerTypes/estimates/estimates.schema";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import Text from "@CubeComponents/html/Text/Text";
import {
  createEstimateVersion,
  getEstimateNumberValueBasedOnSettings,
  getEstimateVersionInfo,
  isEstimateRequiredPermission,
  restoreEstimateVersion,
} from "@Actions/estimation";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { showToastNotification } from "@Reducers/toastSlice";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { getEstimateData } from "@Actions/EstimateList";
import { Accordion, AccordionDetails, AccordionSummary, Box } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useLocation, useParams } from "react-router-dom";
import { getEstimateUrl } from "@Features/projects/utils/projectUrls";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import IconComponent from "@CubeComponents/icons";
import _ from "lodash";
import { defaultModulesMapper, folderPermissions } from "@Helpers/constants";
import { getDefaultCurrencyDetailForProjectLevel } from "@Actions/Currency";
import { AutoSizer, List } from "react-virtualized";
import VersionNotesComponent from "@Components/VersionNotes";
import { SimpleEditor } from "@CubeComponents/RichTextEditor/components/tiptap-templates/simple/ControlledSimpleEditor";
import { sanitizeHtml } from "@Utils/sanitize";
import type { RootState } from "src/reducers";
import ModuleUsersAccess from "@Components/ModuleUsersAccess";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";
const { RESOURCE_MODIFY, RESOURCE_UPLOAD, RESOURCE_VIEW } = folderPermissions();

type EstimateVersionDetails = {
  namedVersions: Array<Array<EstimateVersionEntry>>;
  microVersions: EstimateVersionEntry[];
};

type VersionNotesMap = { [versionNumber: number]: { note: NonNullable<EstimateVersionEntry["note"]> } | undefined };

type EstimateFileDetail = NonNullable<GetEstimateFilesResponse extends { result?: infer R } ? R : never>[number];

type CurrencyData = {
  category?: "Prefix" | "Suffix";
  selectedSymbol?: { name?: string };
};

type EstimateProp = {
  /** Estimates `_id` — `objectIdString` in estimateListItemSchema / estimateDataSchema. */
  _id: ObjectIdString;
  /** EstimateVersions `estimateId` — stored as ObjectId (estimates.collection.ts). */
  estimateId?: ObjectIdString;
};

/**
 * User-permission row stored on `Estimates.users[]` and consumed by `ModuleUsersAccess`.
 * Server SSOT: `estimateUserEntry` in `estimates.collection.ts` (ObjectId on the server,
 * string when serialized to the wire).
 */
type EstimateUserEntry = {
  id: string;
  /** Numeric folderRoles id, as `estimateUserEntry` in server/estimates/estimates.collection.ts declares it. */
  role: number;
  permissions: number[];
};

type EstimateDataDetail = EstimateDataState & {
  createdBy?: { _id?: string };
  createdDate?: number;
  amount?: number;
  costIndex?: number;
  description?: string;
  costDatabase?: { id?: string; name?: string };
  fileHierarchyId?: string;
  space?: { _id?: string };
  userId?: string;
  users: EstimateUserEntry[];
  versionNumber?: number;
  constituentEstimateVersionDetails?: Array<{
    counter: number;
    name?: string;
    versionId?: string;
    versionNumber?: number;
  }>;
};

type VersionListProps = {
  width: number;
  data: EstimateVersionEntry[];
  nestedContent: (items: EstimateVersionEntry[]) => React.ReactNode;
  height?: number;
  rowHeight?: number;
};

const VersionList = ({ width, data, nestedContent, height = 400, rowHeight = 80 }: VersionListProps) => {
  const rowRenderer = ({ key, index, style }: { key: string; index: number; style: React.CSSProperties }) => {
    const item = data[index];
    if (!item) {
      return <div key={key} style={style} />;
    }

    return (
      <div key={key} style={style}>
        {nestedContent([item])}
      </div>
    );
  };

  return <List width={width} height={height} rowCount={data.length} rowHeight={rowHeight} rowRenderer={rowRenderer} />;
};
VersionList.displayName = "VersionList";

function EstimateProperty({
  source,
  estimateprop,
  makeModalInvisible,
  updateStateToRefreshDatasource,
  needPageRefresh,
}: {
  source: string;
  estimateprop: EstimateProp;
  makeModalInvisible?: () => void;
  updateStateToRefreshDatasource: () => void;
  needPageRefresh: boolean;
}) {
  const { t } = useTranslation();

  const [accordianState, setAccordianState] = useState<{
    details: boolean;
    versions: boolean;
    files: boolean;
    costDatabase: boolean;
    mergeEstimate: boolean;
    reviews: boolean;
    accessContent: boolean;
  }>({
    details: false,
    versions: false,
    files: false,
    costDatabase: false,
    mergeEstimate: false,
    reviews: false,
    accessContent: false,
  });
  const [estimateFilesDetails, setEstimateFilesDetails] = useState<EstimateFileDetail[]>([]);
  const [estimateVersionDetails, setEstimateVersionDetails] = useState<EstimateVersionDetails>({
    namedVersions: [],
    microVersions: [],
  });
  const [currencyData, setCurrencyData] = useState<CurrencyData>({});
  const [addNoteModalVisible, setAddNoteModalVisible] = useState(false);
  const [selectedVersionData, setSelectedVersionData] = useState<{
    versionNumber: number;
    estimateVersionId?: string;
  } | null>(null);
  const [editingNote, setEditingNote] = useState<NonNullable<EstimateVersionEntry["note"]> | null | "">("");
  const [versionNotesMap, setVersionNotesMap] = useState<VersionNotesMap>({});
  const dispatch = useDispatch();
  const params = useParams();
  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);
  // Entry point: URLSearchParams.get() is outside the type system, so the id is validated here
  // rather than asserted; `undefined` when the query string carries no usable projectId.
  const projectIdParam = objectIdString.safeParse(urlSearchParams.get("projectId")).data;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- `estimateDataSchema` (packages/server/estimates/estimates.schema.ts:449) declares none of the fields below and is `.passthrough()`, so they reach redux only through its `unknown` index signature; declaring them on that schema is the fix and it is not in this file
  const estimateData = useSelector((state: RootState) => state.estimateData) as EstimateDataDetail;

  type VersionMenuItem = { text: string; id: string; disabled?: boolean };
  const items: VersionMenuItem[] = [{ text: t("Close"), id: "close" }];
  const getVersionMenuItems = (data: EstimateVersionEntry): VersionMenuItem[] => {
    const versionMenuItems: VersionMenuItem[] = [
      {
        text: "Preview",
        id: "previewVersion",
        disabled: !isEstimateRequiredPermission(estimateData, RESOURCE_VIEW.id),
      },
    ];
    versionMenuItems.push({
      text: "Restore",
      id: "restoreVersion",
      disabled: data.estimateType === "merge" || !isEstimateRequiredPermission(estimateData, RESOURCE_MODIFY.id),
    });
    return versionMenuItems;
  };

  const handleSelectedOption = (args: SelectEventArgs) => {
    if (args.item.properties.id == "close") {
      makeModalInvisible?.();
    }
  };

  const handleVersionMenuSelect = async (args: SelectEventArgs, versionData: EstimateVersionEntry) => {
    if (args.item.id === "previewVersion") {
      const popupUrl = getEstimateUrl({
        projectId: params.projectId,
        estimateId: params.estimateId || estimateprop._id,
        versionId: versionData._id,
      });

      window.open(popupUrl, "_blank");
    }
    if (args.item.id === "restoreVersion") {
      const response = await restoreEstimateVersion({
        estimateId: estimateprop._id,
        restoreEstimateVersionId: versionData._id,
      });
      if (response.data.status) {
        globalThis.location.reload();
      }
    }
  };
  const getUserAvatar = (id: string) => <UserNameTagComponent id={id} />;

  const nestedContent = (versionDataList: EstimateVersionEntry[]) => (
    <Box data-testid="versionDataList" className="gap-2">
      {versionDataList.map((versionData, index) => (
        <Box
          key={index}
          className="flex flex-row items-center px-4 py-2 gap-6 w-full overflow-hidden"
          data-testid="microVersionContainer"
        >
          <SelectDropDown
            select={(args) => handleVersionMenuSelect(args, versionData)}
            items={getVersionMenuItems(versionData)}
            cssClass="e-caret-hide p-0 bg-white border-0 m-0"
            disabled={source === "fileManager"}
            data-testid="threeDotsInMicroVersion"
          >
            <BsThreeDotsVertical />
          </SelectDropDown>
          <Box className="flex flex-col items-start gap-2">
            <Box>
              <ToolTipComponent
                showTipPointer
                position="left"
                content={convertDate(versionData.dateVersion, "dateForTooltip")}
                data-testid="microVersionDateForText"
              >
                {convertDate(Number(versionData.dateVersion), "dateForText")}
              </ToolTipComponent>
            </Box>
            <Box>
              <Text className="text-sm">{t("Published by")}</Text> {getUserAvatar(versionData.updatedBy._id)}
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );

  const versionHeaderDropdown = (versionNumber: number) => {
    const handleVersionHeaderDropdown = (args: SelectEventArgs, versionNumber: number) => {
      if (args.item.id === "addNote") {
        handleAddNoteClick(versionNumber);
      } else if (args.item.id === "editNote") {
        const note = versionNotesMap[versionNumber]?.note;
        handleAddNoteClick(versionNumber, note);
      } else if (args.item.id === "deleteNote") {
        handleDeleteNote(versionNumber);
      }
    };
    const handleDropdownClick = (e: { stopPropagation: () => void }) => {
      e.stopPropagation();
    };
    const hasNote = versionNotesMap[versionNumber] !== undefined;
    const dropdownItems = hasNote
      ? [
          { text: t("Edit Note"), id: "editNote" },
          { text: t("Delete Note"), id: "deleteNote" },
        ]
      : [{ text: t("Add Note"), id: "addNote" }];
    return (
      <SelectDropDown
        select={(args) => handleVersionHeaderDropdown(args, versionNumber)}
        items={dropdownItems}
        cssClass="e-caret-hide p-0 bg-white border-0 m-0"
        onClick={handleDropdownClick}
        disabled={!isEstimateRequiredPermission(estimateData, RESOURCE_MODIFY.id)}
      >
        <BsThreeDotsVertical />
      </SelectDropDown>
    );
  };

  const versionHeaderTemplate = (versionNumber?: number) => {
    const isDraft = typeof versionNumber !== "number";
    const content = isDraft ? "Draft" : `V${versionNumber}`;

    return (
      <Box
        className="text-black"
        data-testid="VersionNumberPropHeader"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}
      >
        <strong data-testid="VersionNumberPropLabel">{content}</strong>
        {!isDraft && versionHeaderDropdown(versionNumber)}
      </Box>
    );
  };

  const createNewEstimateVersion = async () => {
    await createEstimateVersion({ estimateId: estimateprop._id });
    await getEstimateVersionDetails();
    dispatch(getEstimateData({ estimateId: estimateprop._id }));
    if (needPageRefresh) {
      updateStateToRefreshDatasource();
    }
  };

  useEffect(() => {
    dispatch(getEstimateData({ estimateId: estimateprop._id }));
    getEstimateFiles();
    getEstimateVersionDetails();
    if (projectIdParam) {
      getCurrenciesData();
    }
  }, [estimateprop._id]);

  const accessUpdateHandler = () => {
    dispatch(getEstimateData({ estimateId: estimateprop._id }));
  };

  const topHead = () => (
    <Box className="flex items-start justify-between" data-testid="topHeadDetails">
      <p>
        {" "}
        <span className="pt-px pb-1 px-1 border border-[rgb(144_144_144)] rounded text-center" data-testid="estimateID">
          {" "}
          {estimateData.counter}{" "}
        </span>{" "}
      </p>
      {source !== "fileManager" && makeModalInvisible && (
        <span className="flex items-center gap-2">
          <SelectDropDown
            select={(args) => handleSelectedOption(args)}
            items={items}
            cssClass="e-caret-hide p-0 bg-white border-0 m-0"
          >
            <BsThreeDotsVertical className="cursor-pointer" data-testid="threeDotsInEstProperties" />
          </SelectDropDown>
        </span>
      )}
    </Box>
  );

  const head = () => (
    <Box className="pb-[4%] ps-[4%]">
      <Box className="flex flex-row w-full items-center justify-start">
        <IconComponent
          name="estimates"
          className="me-1"
          data-testid="estimatePropertyIcon"
          width="1.25rem"
          height="1.25rem"
        />
        <Text className="text-2xl font-semibold">{estimateData.name}</Text>
      </Box>
      <Box className="flex items-center text-sm gap-2" data-testid="estimateDetails">
        {estimateData.createdBy?._id && <UserNameTagComponent id={estimateData.createdBy._id} size="XS" isCircle />}
        <Box className="flex gap-2" data-testid="estimateCreationDetail">
          <Text sx={{ whiteSpace: "normal" }}>{t("created on")}</Text>
          <strong data-testid="estimateDate">{convertDate(Number(estimateData.createdDate), "dateForText")}</strong>
        </Box>
      </Box>
    </Box>
  );

  const detailsHeader = () => (
    <Box className="flex flex-row items-center justify-start px-2" data-testid="detailsHeaderSection">
      <IconComponent name="details" className="me-1" width="1.25rem" height="1.25rem" />
      <Text className="font-bold text-primary_text" data-testid="detailsLabel">
        {t("Details")}
      </Text>
    </Box>
  );

  const constituentEstimateHeader = () => (
    <Box className="flex flex-row items-center justify-start px-2">
      <IconComponent name="constituentEstimates" className="me-1" width="1.25rem" height="1.25rem" />
      <Text className="font-bold text-primary_text">{t("Constituent Estimates")}</Text>
    </Box>
  );

  const estimateDetails = () => (
    <Box className="px-2 mb-2" data-testid="estimatePropertyDetails">
      <Box className="flex flex-row items-center justify-between mb-3 gap-4">
        <Text className="font-bold text-primary_text" data-testid="estimatePropNameLabel">
          {t("Name")}
        </Text>
        <Text className="text-end" data-testid="estimatePropName">
          {estimateData.name}
        </Text>
      </Box>

      <Box className="flex flex-row items-center justify-between mb-3 gap-4">
        <Text className="font-bold text-primary_text" data-testid="estimatePropDescLabel">
          {t("Description")}
        </Text>
        <Text className="max-w-[70%] text-end break-words" data-testid="estimatePropDesc">
          {estimateData.description}
        </Text>
      </Box>

      <Box className="flex flex-row items-center justify-between mb-3 gap-4">
        <Text className="font-bold text-primary_text" data-testid="estimatePropVersionLabel">
          {t("Version")}
        </Text>
        <Text className="max-w-[70%] text-end break-words" data-testid="estimatePropVersion">
          {estimateData.versionNumber ?? "-"}
        </Text>
      </Box>

      <Box className="flex flex-row items-center justify-between mb-3 gap-4">
        <Text className="font-bold text-primary_text" data-testid="estimatePropCreatedOnLabel">
          {t("Created On")}
        </Text>
        <Text className="max-w-[70%] text-end break-words" data-testid="estimatePropCreatedOn">
          {convertDate(Number(estimateData.createdDate), "dateForText")}
        </Text>
      </Box>

      <Box className="flex flex-row items-center justify-between mb-3 gap-4">
        <Text className="font-bold text-primary_text" data-testid="estPropEstValueLabel">
          {t("Estimate Value")}
        </Text>
        <p className="max-w-[70%] break-words" data-testid="estimatePropEstimateValue">
          {currencyData.category === "Prefix" ? _.get(currencyData, "selectedSymbol.name") : null}
          {getEstimateNumberValueBasedOnSettings(estimateData.amount ?? 0, estimateData, "amount")}
          {currencyData.category === "Suffix" ? _.get(currencyData, "selectedSymbol.name") : null}
        </p>
      </Box>

      <Box className="flex flex-row items-center justify-between mb-3 gap-4">
        <Box className="inline-flex gap-1">
          <Text className="font-bold text-primary_text" data-testid="estimatePropCostIndexLabel">
            {t("Cost Index")}
          </Text>
          <IconComponent
            name="details"
            data-testid="defaultCIText"
            position="left"
            toolTipContent={t("The default cost index for this estimate.")}
            width="1.25rem"
            height="1.25rem"
          />
        </Box>
        <Text className="max-w-[70%] text-end break-words" data-testid="estimatePropCostIndex">
          {getEstimateNumberValueBasedOnSettings(estimateData.costIndex ?? 0, estimateData, "quantity")}
        </Text>
      </Box>
    </Box>
  );

  const constituentEstimateDetails = () => (
    <Box className="w-full">
      {estimateData.constituentEstimateVersionDetails?.map((item, index: number) => {
        const name = item.name ?? "-";
        const versionNumber = item.versionNumber ?? "-";
        return (
          <Box key={index} className="flex flex-row items-center gap-10 justify-between mb-3 truncate">
            <Box className="overflow-hidden whitespace-nowrap overflow-ellipsis w-80">
              <strong>{`ES-${item.counter.toString().padStart(4, "0")}`}</strong>
              <span className="ms-2">{name}</span>
            </Box>
            <Box>{`V${versionNumber}`}</Box>
          </Box>
        );
      })}
    </Box>
  );

  const versionHeader = () => (
    <Box className="flex flex-row items-center justify-start px-2" data-testid="versionHeaderSection">
      <IconComponent
        name="VersionsIcon"
        className="me-1"
        data-testid="versionHeaderIcon"
        width="1.25rem"
        height="1.25rem"
      />
      <Text className="font-bold text-primary_text" data-testid="versionLabel">
        {t("Versions")}
      </Text>
    </Box>
  );

  const costDatabaseHeader = () => (
    <Box className="flex flex-row items-center justify-start px-2" data-testid="basedOnCostCodeSection">
      <IconComponent
        name="documentattach"
        className="me-1"
        data-testid="basedOnCostCodeIcon"
        width="1.25rem"
        height="1.25rem"
      />
      <Text className="font-bold text-primary_text" data-testid="basedOnCostCodeLabel">
        {t("Based on Cost Code")}
      </Text>
    </Box>
  );

  const costDatabaseDetails = () => (
    <Box className="ms-5 flex flex-row items-center justify-start" data-testid="costDatabaseDetailPropSection">
      <IconComponent name="documentattach" data-testid="costDatabaseDetailPropImg" width="1.25rem" height="1.25rem" />
      <p data-testid="costDatabaseDetailCDName"> {estimateData.costDatabase?.name} </p>
    </Box>
  );

  const filesHeader = () => (
    <Box className="flex flex-row items-center justify-start px-2" data-testid="filesSection">
      <IconComponent name="files" className="me-1" data-testid="filesSectionIcon" width="1.25rem" height="1.25rem" />
      <Text className="font-bold text-primary_text" data-testid="filesLabel">
        {t("Files")}
      </Text>
    </Box>
  );

  const filesDetails = () => (
    <Box>
      {estimateFilesDetails.length ? (
        estimateFilesDetails.map((data, index) => (
          <Box key={index} className="ms-5 mb-4 flex flex-row items-center justify-start">
            <IconComponent name="files" width="1.25rem" height="1.25rem" />
            <p>{data.fileName}</p>
          </Box>
        ))
      ) : (
        <p data-testid="noRecordsDesc">
          {" "}
          <Text>{t("No Records Found")}</Text>{" "}
        </p>
      )}
    </Box>
  );

  const versionDetails = () => (
    <Box>
      {source !== "fileManager" && estimateVersionDetails.microVersions.length ? (
        <Box className="mb-[1.875rem]">
          <ButtonComponent
            onClick={createNewEstimateVersion}
            disabled={!isEstimateRequiredPermission(estimateData, RESOURCE_UPLOAD.id)}
            data-testid="newBtnInVersionsSec"
          >
            + <Text>{t("New")}</Text>
          </ButtonComponent>
        </Box>
      ) : undefined}
      <Box>
        {estimateVersionDetails.microVersions.length > 0 && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ flexDirection: "row-reverse" }}>
              {versionHeaderTemplate()}
            </AccordionSummary>
            <AccordionDetails>
              <AutoSizer disableHeight>
                {({ width }) => (
                  <VersionList
                    width={width}
                    data={estimateVersionDetails.microVersions}
                    nestedContent={nestedContent}
                  />
                )}
              </AutoSizer>
            </AccordionDetails>
          </Accordion>
        )}
        {estimateVersionDetails.namedVersions.length > 0 &&
          estimateVersionDetails.namedVersions.map((namedVersionData, index: number) => {
            const versionEntry = namedVersionData[0];
            if (!versionEntry) {
              return null;
            }
            const versionNote = versionNotesMap[versionEntry.versionNumber];
            return (
              <Accordion key={index}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ flexDirection: "row-reverse" }}>
                  {versionHeaderTemplate(versionEntry.versionNumber)}
                </AccordionSummary>
                <AccordionDetails>
                  {versionNote && versionNote.note.description.trim() !== "" && (
                    <Box className="flex flex-col items-start border border-surface_stroke_gradient_color1 bg-surface_neutral gap-6 p-4">
                      <Box className="text-sm text-primary_text w-full break-words">
                        <SimpleEditor
                          value={sanitizeHtml(versionNote.note.description)}
                          editable={false}
                          showToolbar={false}
                        />
                      </Box>
                      <Box className="flex justify-between items-center w-full flex-wrap gap-2">
                        <UserNameTagComponent id={versionNote.note.updatedBy} />
                        <Box className="text-xs text-primary_text">
                          {convertDate(versionNote.note.updatedOn, "dateForText")}
                        </Box>
                      </Box>
                    </Box>
                  )}
                  <AutoSizer disableHeight>
                    {({ width }) => <VersionList width={width} data={namedVersionData} nestedContent={nestedContent} />}
                  </AutoSizer>
                </AccordionDetails>
              </Accordion>
            );
          })}
      </Box>
    </Box>
  );

  const estimateBaiscContent = () => (
    <Box className="pb-2 border-0">
      <Accordion
        expanded={accordianState.details}
        onChange={() => setAccordianState((prev) => ({ ...prev, details: !prev.details }))}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>{detailsHeader()}</AccordionSummary>
        <AccordionDetails>{estimateDetails()}</AccordionDetails>
      </Accordion>
    </Box>
  );

  const accessHeader = () => (
    <Box className="flex flex-row items-center justify-start ps-3 pe-2">
      <IconComponent className="me-1" name="access" width="1.25rem" height="1.25rem" />
      <Text className="font-bold text-primary_text">{t("Access")}</Text>
    </Box>
  );

  const accessDetails = () => (
    <ModuleUsersAccess
      module={defaultModulesMapper.ESTIMATES}
      resourceId={estimateprop._id}
      users={estimateData.users}
      spaceId={estimateData.space?._id}
      accessChangeCompleteCallback={accessUpdateHandler}
    />
  );

  const estimateAccessContent = () => (
    <Box className="pb-2 border-0">
      <Accordion
        expanded={accordianState.accessContent}
        onChange={() =>
          setAccordianState((prev) => ({
            ...prev,
            accessContent: !prev.accessContent,
          }))
        }
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>{accessHeader()}</AccordionSummary>
        <AccordionDetails>{accessDetails()}</AccordionDetails>
      </Accordion>
    </Box>
  );

  const estimateVersionContent = () => (
    <Box className="pb-2 border-0">
      <Accordion
        expanded={accordianState.versions}
        onChange={() => setAccordianState((prev) => ({ ...prev, versions: !prev.versions }))}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>{versionHeader()}</AccordionSummary>
        <AccordionDetails>{versionDetails()}</AccordionDetails>
      </Accordion>
    </Box>
  );

  const estimateCostDatabaseContent = () => (
    <Box className="pb-2 border-0">
      <Accordion
        expanded={accordianState.costDatabase}
        onChange={() => setAccordianState((prev) => ({ ...prev, costDatabase: !prev.costDatabase }))}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>{costDatabaseHeader()}</AccordionSummary>
        <AccordionDetails>{costDatabaseDetails()}</AccordionDetails>
      </Accordion>
    </Box>
  );

  const estimateFileContent = () => (
    <Box className="pb-2 border-0">
      <Accordion
        expanded={accordianState.files}
        onChange={() => setAccordianState((prev) => ({ ...prev, files: !prev.files }))}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>{filesHeader()}</AccordionSummary>
        <AccordionDetails>{filesDetails()}</AccordionDetails>
      </Accordion>
    </Box>
  );

  const mergeEstimateContent = () => (
    <Box className="pb-2 border-0">
      <Accordion
        expanded={accordianState.mergeEstimate}
        onChange={() =>
          setAccordianState((prev) => ({
            ...prev,
            mergeEstimate: !prev.mergeEstimate,
          }))
        }
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>{constituentEstimateHeader()}</AccordionSummary>
        <AccordionDetails>{constituentEstimateDetails()}</AccordionDetails>
      </Accordion>
    </Box>
  );

  const getSidebarContent = () => (
    <>
      {estimateBaiscContent()}
      {estimateData.estimateType !== "merge" && estimateAccessContent()}
      {estimateVersionContent()}
      {estimateData.estimateType == "merge" && mergeEstimateContent()}
      {estimateData.estimateType !== "merge" && _.get(estimateData, "costDatabase.id") && estimateCostDatabaseContent()}
      {estimateData.estimateType !== "merge" && estimateFileContent()}
    </>
  );

  const handleDeleteNote = async (versionNumber: number) => {
    const estimateVersionId = estimateVersionDetails.namedVersions.find(
      (versions) => versions[0]?.versionNumber === versionNumber,
    )?.[0]?._id;
    if (!estimateVersionId) {
      return;
    }
    const payload: DeleteEstimateVersionNoteRequest = {
      estimateId: estimateprop._id,
      estimateVersionId,
      versionNumber: versionNumber,
    };
    const res = await axiosWrapper.post<DeleteEstimateVersionNoteResponse>(
      `${getBackendUrl()}/estimate/deleteEstimateVersionNote`,
      payload,
    );
    if (res.data.status) {
      const updatedNotesMap = { ...versionNotesMap };
      delete updatedNotesMap[versionNumber];
      setVersionNotesMap(updatedNotesMap);

      dispatch(
        showToastNotification({
          content: t("Note deleted from Estimate Version V{{versionNumber}}", { versionNumber: versionNumber }),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: t("Note could not be deleted"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const handleAddNoteClick = (versionNumber: number, existingNote?: NonNullable<EstimateVersionEntry["note"]>) => {
    const versionData = estimateVersionDetails.namedVersions.find(
      (versions) => versions[0]?.versionNumber === versionNumber,
    );

    setSelectedVersionData({
      versionNumber,
      estimateVersionId: versionData?.[0]?._id,
    });

    if (existingNote) {
      setEditingNote(existingNote);
    } else {
      setEditingNote(null);
    }

    setAddNoteModalVisible(true);
  };

  const handleAddNoteCancel = () => {
    setAddNoteModalVisible(false);
    setSelectedVersionData(null);
    setEditingNote(null);
  };

  async function getEstimateFiles() {
    const payLoad = { value: { params: { estimateId: estimateprop._id } } };
    const res = await axiosWrapper.post<GetEstimateFilesResponse>(
      `${getBackendUrl()}/estimate/getEstimateFiles`,
      payLoad,
    );
    if (res.data.status) {
      setEstimateFilesDetails(res.data.result ?? []);
    }
  }

  async function getEstimateVersionDetails() {
    const res = await getEstimateVersionInfo({
      estimateId: estimateprop.estimateId || estimateprop._id,
    });

    const namedVersions = res.data.namedVersions ?? [];
    const microVersions = res.data.microVersions ?? [];
    setEstimateVersionDetails({ namedVersions, microVersions });
    const notesMap: VersionNotesMap = {};
    for (const version of namedVersions) {
      const firstEntry = version[0];
      if (firstEntry?.note) {
        notesMap[firstEntry.versionNumber] = {
          note: firstEntry.note,
        };
      }
    }
    setVersionNotesMap(notesMap);
  }

  async function getCurrenciesData() {
    if (!projectIdParam) return;
    const response = await getDefaultCurrencyDetailForProjectLevel({
      projectId: projectIdParam,
    });
    if (response.data.status && response.data.currencyDetail) {
      setCurrencyData({
        ...response.data.currencyDetail,
        selectedSymbol: response.data.symbolDetail,
      });
    }
  }

  return (
    estimateData._id && (
      <Box
        className="
            flex flex-col justify-between
            h-full w-full
            bg-white
            rounded-md
            shadow-[0_0.25rem_0.625rem_0_rgb(0_0_0_/_0.12)]
            border border-[#ecf3fe]
            overflow-auto
          "
      >
        <Box className="flex flex-col h-[6.625rem] ps-2.5 pt-5 pb-[1.875rem] gap-3">
          <Box>{topHead()}</Box>
          <Box>{head()}</Box>
          <Box className="flex flex-row justify-between h-full">
            <Box className="w-full ">{getSidebarContent()}</Box>
            <Box className="flex flex-col h-full gap-6 items-center pt-5 border border-surface_stroke_gradient_color1">
              <IconComponent
                name="details"
                className="mt-0 mx-2.5 mb-[-0.3125rem]"
                onClick={() => {
                  setAccordianState(() => ({
                    details: true,
                    versions: false,
                    files: false,
                    costDatabase: false,
                    mergeEstimate: false,
                    reviews: false,
                    accessContent: false,
                  }));
                }}
                width="1.5rem"
                height="1.5rem"
              />
              {estimateData.estimateType !== "merge" && (
                <IconComponent
                  name="access"
                  onClick={() => {
                    setAccordianState(() => ({
                      details: false,
                      versions: false,
                      files: false,
                      costDatabase: false,
                      mergeEstimate: false,
                      reviews: false,
                      accessContent: true,
                    }));
                  }}
                  width="1.25rem"
                  height="1.25rem"
                />
              )}
              <IconComponent
                name="VersionsIcon"
                className="mt-0 mx-2.5 mb-[-0.3125rem]"
                onClick={() => {
                  setAccordianState(() => ({
                    details: false,
                    versions: true,
                    files: false,
                    costDatabase: false,
                    mergeEstimate: false,
                    reviews: false,
                    accessContent: false,
                  }));
                }}
                width="1.5rem"
                height="1.5rem"
              />
              {estimateData.estimateType == "merge" && (
                <IconComponent
                  name="constituentEstimates"
                  className="mt-0 mx-2.5 mb-[-0.3125rem]"
                  onClick={() => {
                    setAccordianState(() => ({
                      details: false,
                      versions: false,
                      files: false,
                      costDatabase: false,
                      mergeEstimate: true,
                      reviews: false,
                      accessContent: false,
                    }));
                  }}
                  width="1.5rem"
                  height="1.5rem"
                />
              )}
              {estimateData.estimateType !== "merge" && !!_.get(estimateData, "costDatabase.id") && (
                <IconComponent
                  name="documentattach"
                  className="mt-0 mx-2.5 mb-[-0.3125rem]"
                  onClick={() => {
                    setAccordianState(() => ({
                      details: false,
                      versions: false,
                      files: false,
                      costDatabase: true,
                      mergeEstimate: false,
                      reviews: false,
                      accessContent: false,
                    }));
                  }}
                  width="1.5rem"
                  height="1.5rem"
                />
              )}
              {estimateData.estimateType !== "merge" && (
                <IconComponent
                  name="files"
                  className="mt-0 mx-2.5 mb-[-0.3125rem]"
                  onClick={() => {
                    setAccordianState(() => ({
                      details: false,
                      versions: false,
                      files: true,
                      costDatabase: false,
                      mergeEstimate: false,
                      reviews: false,
                      accessContent: false,
                    }));
                  }}
                  width="1.5rem"
                  height="1.5rem"
                />
              )}
            </Box>
          </Box>
        </Box>
        <VersionNotesComponent
          isVisible={addNoteModalVisible}
          onClose={handleAddNoteCancel}
          module="estimate"
          moduleData={{
            id: estimateprop._id,
            versionId: selectedVersionData?.estimateVersionId,
            noteId:
              selectedVersionData?.versionNumber !== undefined ? String(selectedVersionData.versionNumber) : undefined,
          }}
          existingNote={typeof editingNote === "object" && editingNote !== null ? editingNote.description : undefined}
          onSuccess={() => {
            getEstimateVersionDetails();
          }}
        />
      </Box>
    )
  );
}

export default EstimateProperty;
